import { useCallback, useRef, useState } from 'react';
import { Checkout } from '../types/checkout';
import { Api } from './useApi';
import { MetodoPagamentoVendaEnum } from '../types/enums/metodo-pagamento.enum';
import { MoedaVendaDescricao, MoedaVendaEnum } from '../types/enums/moeda-venda.enum';
import { getAllUrlParams, getUrlParams } from '../utils/get-url-params';
import { emitUtmifyEvent } from '../utils/emitUtmifyEvent';
import { getConfig } from '../utils/get-config';

type Params = {
  checkout: Checkout;
  api: Api;
  gerarTokenRecaptcha: (path: string) => Promise<string>;
  onSuccess: (vendaId: string, vendaToken: string) => void;
  onError: (msg: string) => void;
};

type Result = {
  paypalSubscriptionAvailable: boolean;
  paypalSubscriptionLoading: boolean;
  paypalSubscriptionCurrency: string;
  createSubscription: () => Promise<string>;
  onApprove: (data: { subscriptionID: string }) => Promise<void>;
  onCancel: () => void;
};

export const usePayPalSubscription = ({
  checkout,
  api,
  gerarTokenRecaptcha,
  onSuccess,
  onError,
}: Params): Result => {
  const [loading, setLoading] = useState(false);
  const vendaIdRef = useRef<string | null>(null);
  const vendaTokenRef = useRef<string | null>(null);

  const sdkConfigured = !!getConfig('REACT_APP_PAYPAL_CLIENT_ID');

  const paypalSubscriptionAvailable =
    sdkConfigured &&
    !!checkout?.id &&
    !!checkout?.metodosPagamento?.includes('paypal') &&
    !!checkout?.recorrencia;

  const paypalSubscriptionCurrency =
    MoedaVendaDescricao[checkout?.moeda ?? MoedaVendaEnum.BRL] || 'BRL';

  const createSubscription = useCallback(async (): Promise<string> => {
    setLoading(true);
    try {
      const recaptchaToken = await gerarTokenRecaptcha(
        '/venda/paypal/create_subscription',
      );
      const afiliadoId = getUrlParams('affiliate') ?? undefined;

      const res = await api.Venda.createPaypalSubscription({
        metodoPagamento: MetodoPagamentoVendaEnum.PAY_PAL,
        produtoId: checkout.produtoId ?? '',
        emailComprador: '',
        nomeComprador: '',
        sobrenomeComprador: '',
        cpfComprador: '',
        telefoneComprador: '',
        checkoutId: checkout.id,
        recaptchaToken,
        afiliadoId,
        utmSource: getUrlParams('utm_source'),
        utmCampaign: getUrlParams('utm_campaign'),
        utmMedium: getUrlParams('utm_medium'),
        utmContent: getUrlParams('utm_content'),
        utmTerm: getUrlParams('utm_term'),
        urlParams: getAllUrlParams(),
      });

      if (res.error || !res.data?.subscriptionId) {
        const msg =
          (res as any).message?.[0] ?? 'Erro ao criar assinatura PayPal.';
        onError(msg);
        throw new Error(msg);
      }

      vendaIdRef.current = res.data.vendaId;
      vendaTokenRef.current = res.data.vendaToken;
      emitUtmifyEvent({
        event: 'waiting_payment',
        vendaId: res.data.vendaId,
      });
      return res.data.subscriptionId;
    } catch (err: any) {
      setLoading(false);
      throw err;
    }
  }, [checkout?.id, checkout?.produtoId, api, gerarTokenRecaptcha, onError]);

  const onApprove = useCallback(
    async (data: { subscriptionID: string }) => {
      try {
        const vendaId = vendaIdRef.current;
        if (!vendaId) {
          onError('Venda não encontrada.');
          return;
        }

        await api.Venda.activatePaypalSubscription({
          vendaId,
          subscriptionId: data.subscriptionID,
        });

        onSuccess(vendaId, vendaTokenRef.current ?? '');
      } catch (err: any) {
        console.error('[PayPalSubscription] Erro ao ativar:', err);
        onError(
          err?.message ||
            'Erro ao processar assinatura PayPal. Tente novamente.',
        );
      } finally {
        setLoading(false);
      }
    },
    [api, onSuccess, onError],
  );

  const onCancel = useCallback(() => {
    setLoading(false);
  }, []);

  return {
    paypalSubscriptionAvailable,
    paypalSubscriptionLoading: loading,
    paypalSubscriptionCurrency: paypalSubscriptionCurrency,
    createSubscription,
    onApprove,
    onCancel,
  };
};
